// DealsPage.jsx or .tsx
import {
  GridCellParams,
  GridDensity,
  GridRowClassNameParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useQueryClient } from '@tanstack/react-query'
import { useDeleteDealItems } from 'api/aws/organizations_table/useDeleteDealItems'
import { useDeleteItems } from 'api/aws/organizations_table/useDeleteItems'
import { useEntitiesFromSearch } from 'api/search'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useStrategies } from 'api/aws/useStrategies'
import CloneDeal from 'components/CreateDeal/CloneDeal'
import { DealsGridFooter } from 'components/Deals/grid/DealsGridFooter'
import { gridContainerStyles } from 'components/Deals/style'
import { calculateWeightedTotal } from 'components/DataGrid/operators/calculateWeightedTotal'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import '../deals.css'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { isActiveThisMonth } from 'components/DataGrid/operators/isActiveThisMonth'
import { useTreeExpansionPersistence } from 'components/DataGrid/useTreeExpansionPersistence'
import { defaultColumnOrder, defaultFilterModel, defaultSortModel, initialVisibilityState } from './defaultModels'
import { getDealCellClassNames } from './getDealCellClassNames'
import { getDealRowClassNames } from './getDealRowClassNames'
import { useDealGridColumns } from './useDealGridColumns'

const DealsPage = () => {
  const _queryClient = useQueryClient()
  const navigate = useNavigate()
  const { isExpanded, toggleExpanded } = useTreeExpansionPersistence('deals')
  const { pinned } = useLayoutContext()
  const { showSnackbar } = useSnackbarContext()
  const { deals, organizations, products, custom_products: _custom_products } = useEntitiesFromSearch()
  const visibleProducts = useMemo(() => {
    return products.filter(p => isExpanded(`DEAL_${p.deal_uuid}`))
  }, [products, isExpanded])
  const { strategies } = useStrategies(true, ['strategies'])
  const { data: users } = useUserItems()
  const [densityModel, setDensityModel] = useState(localStorage.getItem('dealsDensityModel') ?? 'standard')
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [relatedPredecessorId, setRelatedPredecessorId] = useState(null)
  const [relatedSuccessorId, setRelatedSuccessorId] = useState(null)
  const [isCloneDealOpen, setIsCloneDealOpen] = useState(false)
  const [cloneMode, setCloneMode] = useState('')
  const { mutate: deleteItems } = useDeleteDealItems()
  const { mutate: deleteProductItems } = useDeleteItems()
  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('dealsDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })
  const { dealGridColumns } = useDealGridColumns()
  const apiRef = useGridApiRef()
  const [anchorEl, setAnchorEl] = useState(null)

  const processedDeals = useMemo(() => {
    if (!deals || !organizations) return []
    return deals.map(deal => {
      const organization = organizations.find(org => org.PK === deal.PK)
      const relatedProducts = (deal.relatedProducts ?? []).filter(r => isActiveThisMonth(r.startDate, r.endDate))
      //@ts-ignore
      const relatedStrategy = strategies?.find(strategy => strategy?.id === deal?.deal_uuid)
      const contractedBy = organizations.find(org => org.org_uuid === organization?.contractedBy?.[0])
      const hasChildRows = relatedProducts.length > 0
      const ownerId =
        deal?.owner && Array.isArray(deal?.owner)
          ? deal?.owner?.[0]
          : deal?.owner && !Array.isArray(deal?.owner)
          ? deal?.owner
          : null
      const owner = users?.find(u => u?.user_uuid === ownerId) ?? null
      return {
        ...deal,
        rowId: deal.SK,
        organization,
        contractedBy,
        relatedStrategy,
        relatedProducts,
        hasChildRows,
        owner,
        //@ts-ignore
        weightedTotal: calculateWeightedTotal(deal),
      }
    })
  }, [deals, organizations, strategies])

  const columns = useMemo(() => dealGridColumns(processedDeals, density, navigate), [
    organizations,
    users,
    processedDeals,
    products,
    density,
    navigate,
    toggleExpanded,
  ])
  const changeRowSelectionModel = (newSelectionModel: GridRowSelectionModel) => {
    if (newSelectionModel?.ids?.size === 1) {
      const selectedDeal = deals?.find(d => d?.SK === Array.from(newSelectionModel?.ids)?.[0])
      setSelectedDeal(selectedDeal)
    }
  }
  const handleDeleteClick = (rowSelectionModel: GridRowSelectionModel) => {
    const selectedRowId = Array.from(rowSelectionModel?.ids)?.[0]
    const rowElement = apiRef.current.getRowElement(selectedRowId)
    setAnchorEl(rowElement)
  }
  const handleClosePopover = (rowSelectionModel: GridRowSelectionModel) => {
    setAnchorEl(null)
    if (Array.from(rowSelectionModel?.ids)?.length > 0) {
      Array.from(rowSelectionModel?.ids).forEach(id => {
        apiRef.current.selectRow(id, false)
      })
      setSelectedDeal(null)
      setRelatedPredecessorId(null)
      setRelatedSuccessorId(null)
    }
  }
  const handleConfirmDelete = async (rowSelectionModel: GridRowSelectionModel) => {
    // Clear the selection FIRST (prevents MissingRowIdError when rows vanish)
    try {
      if (apiRef.current?.setRowSelectionModel) {
        apiRef.current.setRowSelectionModel({ ids: new Set<string>(), type: 'include' } as any)
      }
    } catch {
      // intentionally empty
    }

    const selectionId = Array.from(rowSelectionModel?.ids ?? [])?.[0] as string | undefined
    const deal_uuid = selectionId?.split('DEAL_')?.[1]
    setIsDeleting(true)

    const dealToDelete = Array.from(rowSelectionModel?.ids ?? [])
      .map(id => deals.find(deal => deal.SK === id))
      .filter(Boolean)
      .map(deal =>
        deal?.predecessorId
          ? { PK: deal!.PK, SK: deal!.SK, predecessorId: deal!.predecessorId }
          : { PK: deal!.PK, SK: deal!.SK }
      )

    const productsToDelete = deal_uuid
      ? products.filter(p => p.deal_uuid === deal_uuid).map(p => ({ PK: p.PK, SK: p.SK }))
      : []

    const itemsToDelete = [...dealToDelete, ...productsToDelete]

    if (!itemsToDelete.length) {
      showSnackbar('No valid deals selected for deletion.', 'error')
      setIsDeleting(false)
      return
    }

    deleteItems(
      { EntityType: 'deal', items: itemsToDelete },
      {
        onSuccess: data => {
          setAnchorEl(null)
          setSelectedDeal(null)
          setRelatedPredecessorId(null)
          setRelatedSuccessorId(null)

          const msg = productsToDelete.length
            ? `${dealToDelete.length} deal${dealToDelete.length > 1 ? 's' : ''} & ${productsToDelete.length} product${
                productsToDelete.length > 1 ? 's' : ''
              } deleted successfully`
            : `${dealToDelete.length} deal${dealToDelete.length > 1 ? 's' : ''} deleted successfully`

          showSnackbar(msg, 'success')
          setIsDeleting(false)
        },
        onError: err => {
          console.error('Failed to delete deal(s):', err)
          showSnackbar(err?.message || 'Failed to delete deal(s)', 'error')
          setIsDeleting(false)
        },
      }
    )
  }

  const handleEditClick = () => {
    if (selectedDeal) {
      navigate(`/deals/${selectedDeal?.PK}/${selectedDeal?.SK}/agreement`)
    }
  }
  const handleCloneDeal = (mode: string) => {
    if (selectedDeal && mode) {
      setIsCloneDealOpen(true)
      console.log('selectedDeal', selectedDeal)
      setCloneMode(mode)
    }
  }
  const handleCloseCloneDeal = () => {
    setSelectedDeal(null)
    setIsCloneDealOpen(false)
    setCloneMode('')
    setRelatedPredecessorId(null)
    setRelatedSuccessorId(null)
  }
  const changeDensity = (newDensity: GridDensity) => {
    setDensity(newDensity)
  }
  const FooterWrapper = ({
    rowSelectionModel,
    setRowSelectionModel,
  }: {
    rowSelectionModel: GridRowSelectionModel
    setRowSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>
  }) => {
    return (
      <DealsGridFooter
        rowSelectionModel={rowSelectionModel}
        deals={processedDeals}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        onClickDelete={handleDeleteClick}
        handleConfirmDelete={handleConfirmDelete}
        handleClosePopover={handleClosePopover}
        handleEditClick={handleEditClick}
        handleClone={handleCloneDeal}
        selectedData={selectedDeal}
        apiRef={apiRef}
      />
    )
  }
  // const treeRows = useMemo(() => {
  //   if (!processedDeals || !organizations) return []
  //   return buildDealTreeRows(processedDeals, isExpanded)
  // }, [processedDeals, organizations, isExpanded])
  const defaultColumnWidths = Object.fromEntries(
    columns
      .filter(col => typeof col.width === 'number') // only include columns that define a width
      .map(col => [col.field, col.width])
  )
  const getRowClassNameWrapper = (params: GridRowClassNameParams, rowSelectionModel: GridRowSelectionModel) =>
    getDealRowClassNames(params, rowSelectionModel, selectedDeal)
  const getCellClassNameWrapper = (params: GridCellParams) => getDealCellClassNames(params)
  const gridContainerSx = {
    position: 'relative',
    ...gridContainerStyles,
    pt: pinned ? '60px' : 0.8,
    pb: 2,
    transition: 'all 0.3s ease-in-out',
    height: '100%',
  }
  return (
    <>
      <GlobalDataGrid
        // rows={treeRows}
        rows={processedDeals}
        columns={columns}
        isExpanded={isExpanded}
        apiRef={apiRef}
        context={'deals'}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={initialVisibilityState}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        onRowSelectionModelChange={changeRowSelectionModel}
        aggregationModel={{
          total: 'sum',
          netTotal: 'sum',
          weightedTotal: 'sum',
          implementationFee: 'sum',
          activeProducts: 'sum',
          probability: 'avg',
        }}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={FooterWrapper}
        onChangeDensity={changeDensity}
        getRowClassName={getRowClassNameWrapper}
        getCellClassName={getCellClassNameWrapper}
        gridContainerSx={gridContainerSx}
        treeData={false}
      />
      {isCloneDealOpen && selectedDeal && (
        <CloneDeal
          open={isCloneDealOpen}
          onClose={handleCloseCloneDeal}
          prefillData={{ ...selectedDeal, selectedOrganization: selectedOrganization }}
          cloneMode={cloneMode}
          apiRef={apiRef}
        />
      )}
    </>
  )
}

export default DealsPage
